/**
 * TimesheetDetail — saisie des heures / bonus / retenues ponctuelles / notes par employé (Batch #14.251).
 * Aucun montant calculé n'est affiché : le client saisit, le gestionnaire calcule.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Loader2, Lock, Plus, Save, Send, Trash2, UserPlus, Download } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders, Field, inputCls, isDemoResponse, saveBtnCls, cancelBtnCls, deleteBtnCls, FREQ_LABELS, ClientStatus, fmtDate, num, err, portalDownload } from "./_shared";

const HOURS = [["regular_hours", "Heures régulières"], ["ot15_hours", "Temps et demi (× 1,5)"], ["ot2_hours", "Temps double (× 2)"], ["holiday_hours", "Congé férié (h)"], ["sick_hours", "Maladie (h)"], ["vacation_hours", "Vacances (h)"]];
const AMOUNTS = [["bonus", "Boni / prime ($)"], ["commission", "Commission ($)"], ["vacation_payout", "Paie de vacances ($)"]];

function toForm(en, dedMap) {
  const f = {};
  [...HOURS, ...AMOUNTS].forEach(([k]) => { f[k] = en[k] ?? 0; });
  f.other_earnings = (en.other_earnings || []).map((o) => ({ label: o.label || "", amount: o.amount ?? "" }));
  f.one_time_deductions = (en.one_time_deductions || []).map((o) => ({ label: o.label || dedMap[o.code] || "", amount: o.amount ?? "", code: o.code }));
  f.note = en.note || ""; f.excluded = !!en.excluded;
  f.rate_hours = en.rate_hours || [];
  return f;
}

const RATE_H = [["regular_hours", "Rég."], ["ot15_hours", "× 1,5"], ["ot2_hours", "× 2"]];

function RateHours({ rates, rows, onChange, locked, eid }) {
  if (!rates?.length) return null;
  const rowOf = (code) => (rows || []).find((x) => x.code === code) || { code, regular_hours: "", ot15_hours: "", ot2_hours: "" };
  const update = (code, k, v) => onChange([...(rows || []).filter((x) => x.code !== code), { ...rowOf(code), [k]: v }]);
  return (
    <div className="rounded-xl border bg-gray-50 p-3 space-y-2" data-testid={`paie-portal-ts-rates-${eid}`}>
      <div className="text-[11px] font-semibold text-gray-600">Heures aux autres taux <span className="font-normal text-gray-400">(ex. fin de semaine — en plus des heures ci-dessus)</span></div>
      {rates.map((r) => {
        const row = rowOf(r.code);
        return (
          <div key={r.code} className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end" data-testid={`paie-portal-ts-rate-row-${eid}-${r.code}`}>
            <div className="text-xs text-gray-700 sm:pb-2"><b>{r.label}</b> <span className="text-gray-400">· {Number(r.hourly_rate).toFixed(2)} $/h</span></div>
            {RATE_H.map(([k, l]) => (
              <Field key={k} label={l}>
                <input type="number" inputMode="decimal" step="0.25" min="0" className={inputCls} value={row[k] ?? ""} disabled={locked} onChange={(ev) => update(r.code, k, ev.target.value)} data-testid={`paie-portal-ts-rate-${k}-${eid}-${r.code}`} />
              </Field>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ListEditor({ title, items, onChange, locked, placeholder, testId }) {
  const setItem = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="text-[11px] font-semibold text-gray-600">{title}</div>
      {items.map((o, i) => (
        <div key={i} className="flex gap-2 items-center">
          <div className="flex-1 min-w-0"><input className={inputCls} placeholder={placeholder} value={o.label} disabled={locked} onChange={(e) => setItem(i, "label", e.target.value)} data-testid={`${testId}-label-${i}`} /></div>
          <div className="w-28 flex-shrink-0"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} placeholder="$" value={o.amount} disabled={locked} onChange={(e) => setItem(i, "amount", e.target.value)} data-testid={`${testId}-amount-${i}`} /></div>
          {!locked && <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className={deleteBtnCls}><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
      {!locked && (
        <button type="button" onClick={() => onChange([...items, { label: "", amount: "" }])} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 py-1.5" data-testid={`${testId}-add`}>
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      )}
    </div>
  );
}

function EmployeeCard({ row, f, locked, dirty, setField, onRemove, primaryColor }) {
  const e = row.employee;
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl p-4 space-y-3 ${f.excluded ? "bg-gray-50 opacity-70" : "bg-white"}`} data-testid={`paie-portal-ts-card-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-gray-900 flex items-center gap-2">{e.display_name}{dirty && <span className="w-2 h-2 rounded-full bg-amber-500" title="Modifications non enregistrées" />}</div>
          <div className="text-xs text-gray-500">{e.employee_no}{e.job_title ? ` · ${e.job_title}` : ""} · habituellement {e.hours_per_period} h</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={f.excluded} disabled={locked} onChange={(ev) => setField("excluded", ev.target.checked)} data-testid={`paie-portal-ts-excluded-${e.id}`} /> Aucune paie
          </label>
          {!locked && <button type="button" onClick={onRemove} className={deleteBtnCls} title="Retirer de cette période" data-testid={`paie-portal-ts-remove-${e.id}`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      {!f.excluded && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {HOURS.map(([k, l]) => (
              <Field key={k} label={l}>
                <input type="number" inputMode="decimal" step="0.25" min="0" className={inputCls} value={f[k]} disabled={locked} onChange={(ev) => setField(k, ev.target.value)} data-testid={`paie-portal-ts-${k}-${e.id}`} />
              </Field>
            ))}
          </div>
          <RateHours rates={e.extra_rates} rows={f.rate_hours} onChange={(v) => setField("rate_hours", v)} locked={locked} eid={e.id} />
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-medium underline" style={{ color: primaryColor }} data-testid={`paie-portal-ts-more-${e.id}`}>
            {open ? "Masquer" : "Boni, retenues exceptionnelles et note"} {f.one_time_deductions.length + f.other_earnings.length > 0 && !open ? `(${f.one_time_deductions.length + f.other_earnings.length})` : ""}
          </button>
          {open && (
            <div className="space-y-3 pt-2 border-t">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {AMOUNTS.map(([k, l]) => (
                  <Field key={k} label={l}>
                    <input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f[k]} disabled={locked} onChange={(ev) => setField(k, ev.target.value)} data-testid={`paie-portal-ts-${k}-${e.id}`} />
                  </Field>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <ListEditor title="Autres gains exceptionnels" items={f.other_earnings} locked={locked} placeholder="Ex. : prime de quart" onChange={(v) => setField("other_earnings", v)} testId={`paie-portal-ts-earn-${e.id}`} />
                <ListEditor title="Retenues exceptionnelles (cette paie)" items={f.one_time_deductions} locked={locked} placeholder="Ex. : avance à rembourser" onChange={(v) => setField("one_time_deductions", v)} testId={`paie-portal-ts-ded-${e.id}`} />
              </div>
              <Field label="Note pour cet employé"><input className={inputCls} value={f.note} disabled={locked} onChange={(ev) => setField("note", ev.target.value)} placeholder="Ex. : 2 jours d'absence non payés" data-testid={`paie-portal-ts-note-${e.id}`} /></Field>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function TimesheetDetail({ runId, summary, primaryColor, onBack }) {
  const [data, setData] = useState(null);
  const [forms, setForms] = useState({});
  const [dirty, setDirty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [addId, setAddId] = useState("");
  const dedMap = Object.fromEntries((summary?.deduction_types || []).map((d) => [d.code, d.label]));

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/paie/runs/${runId}`, { headers: portalHeaders() });
      setData(r.data);
      setForms(Object.fromEntries(r.data.rows.map((x) => [x.employee.id, toForm(x.entry, dedMap)])));
      setDirty({});
      setNote(r.data.run.note || "");
    } catch (e) { err(e, "Feuille de temps introuvable"); onBack(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, onBack]);
  useEffect(() => { load(); }, [load]);

  const run = data?.run;
  const locked = !run || !run.editable;
  const setField = (eid, k, v) => { setForms((f) => ({ ...f, [eid]: { ...f[eid], [k]: v } })); setDirty((d) => ({ ...d, [eid]: true })); };
  const hasDirty = Object.values(dirty).some(Boolean) || (run && note !== (run.note || ""));

  const saveAll = async () => {
    let demo = false;
    for (const eid of Object.keys(dirty).filter((k) => dirty[k])) {
      const f = forms[eid];
      const body = { ...f };
      [...HOURS, ...AMOUNTS].forEach(([k]) => { body[k] = num(f[k]); });
      body.other_earnings = f.other_earnings.filter((o) => num(o.amount) > 0).map((o) => ({ label: o.label, amount: num(o.amount) }));
      body.one_time_deductions = f.one_time_deductions.filter((o) => num(o.amount) > 0).map((o) => ({ label: o.label, amount: num(o.amount), code: o.code }));
      body.rate_hours = (f.rate_hours || []).filter((x) => x.code).map((x) => ({ code: x.code, regular_hours: num(x.regular_hours), ot15_hours: num(x.ot15_hours), ot2_hours: num(x.ot2_hours) }));
      const res = await axios.put(`${API}/portal/paie/runs/${runId}/entries/${eid}`, body, { headers: portalHeaders() });
      demo = demo || !!res.data?.demo_mode;
    }
    if (run && note !== (run.note || "")) await axios.put(`${API}/portal/paie/runs/${runId}/note`, { note }, { headers: portalHeaders() });
    return demo;
  };

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { const demo = await fn(); if (demo) toast.message("Mode démo : action simulée, rien n'est enregistré."); else if (okMsg) toast.success(okMsg); await load(); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const save = () => act("save", saveAll, "Heures enregistrées");
  const submit = () => window.confirm("Soumettre cette feuille de temps à votre gestionnaire ? Vous pourrez encore la modifier tant qu'elle n'est pas traitée.")
    && act("submit", async () => { const demo = await saveAll(); const res = await axios.post(`${API}/portal/paie/runs/${runId}/submit`, {}, { headers: portalHeaders() }); return demo || isDemoResponse(res); }, "Feuille de temps soumise — votre gestionnaire est avisé");
  const addEmp = () => addId && act("add", async () => { const res = await axios.post(`${API}/portal/paie/runs/${runId}/employees/${addId}`, {}, { headers: portalHeaders() }); setAddId(""); return !!res.data?.demo_mode; }, "Employé ajouté");
  const removeEmp = (eid) => window.confirm("Retirer cet employé de cette période ?") && act(`rm-${eid}`, async () => { const res = await axios.delete(`${API}/portal/paie/runs/${runId}/employees/${eid}`, { headers: portalHeaders() }); return !!res.data?.demo_mode; }, "Employé retiré");

  if (!data) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300 mx-auto" /></div>;
  return (
    <div className="space-y-4" data-testid="paie-portal-ts-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 py-2" data-testid="paie-portal-ts-back"><ArrowLeft className="w-4 h-4" /> Toutes les feuilles de temps</button>

      <div className="bg-white border rounded-2xl p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">Période du {run.period_start} au {run.period_end} <ClientStatus status={run.client_status} testId="paie-portal-ts-status" />{locked && <Lock className="w-3.5 h-3.5 text-gray-400" />}</h3>
            <p className="text-xs text-gray-500 mt-0.5">Paie le {fmtDate(run.pay_date)} · {FREQ_LABELS[run.frequency]} · {data.rows.length} employé(s){run.client_submitted_at ? ` · soumise le ${run.client_submitted_at.slice(0, 10)}` : ""}</p>
          </div>
        </div>
        {locked ? (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="paie-portal-ts-locked">
            Cette période a été traitée par votre gestionnaire. {run.docs.length > 0 ? "Les documents sont disponibles ci-dessous et dans l'onglet « Documents reçus »." : "Les documents apparaîtront dans « Documents reçus » lorsqu'ils vous seront transmis."}
            {run.docs.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                {run.docs.map((d) => (
                  <button key={d.kind} type="button" onClick={() => portalDownload(`/portal/paie/runs/${run.id}/doc/${d.kind}`, `${d.kind}_${run.pay_date}.${d.ext}`, (b) => setBusy(b ? `dl-${d.kind}` : ""))}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50" data-testid={`paie-portal-ts-dl-${d.kind}`}>
                    {busy === `dl-${d.kind}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Field label="Note spéciale pour cette paie (visible par votre gestionnaire)">
            <textarea className={`${inputCls} min-h-[60px]`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. : Marc a été absent 2 jours, prime de fin d'année pour tous…" data-testid="paie-portal-ts-run-note" />
          </Field>
        )}
      </div>

      <div className="space-y-3" data-testid="paie-portal-ts-rows">
        {data.rows.map((row) => forms[row.employee.id] && (
          <EmployeeCard key={row.employee.id} row={row} f={forms[row.employee.id]} locked={locked} dirty={!!dirty[row.employee.id]} primaryColor={primaryColor}
            setField={(k, v) => setField(row.employee.id, k, v)} onRemove={() => removeEmp(row.employee.id)} />
        ))}
        {data.rows.length === 0 && <p className="text-sm text-gray-500 text-center py-6" data-testid="paie-portal-ts-empty">Aucun employé dans cette période — ajoutez-en ci-dessous.</p>}
      </div>

      {!locked && (
        <div className="bg-white border rounded-2xl p-4 space-y-3">
          {data.addable.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2">
              <select className={inputCls} value={addId} onChange={(e) => setAddId(e.target.value)} data-testid="paie-portal-ts-add-select">
                <option value="">Ajouter un employé à cette période…</option>
                {data.addable.map((e) => <option key={e.id} value={e.id}>{`${e.display_name} (${FREQ_LABELS[e.pay_frequency]})`}</option>)}
              </select>
              <button type="button" onClick={addEmp} disabled={!addId || busy === "add"} className={cancelBtnCls} data-testid="paie-portal-ts-add-btn"><UserPlus className="w-4 h-4 inline mr-1" /> Ajouter</button>
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button type="button" onClick={save} disabled={!hasDirty || !!busy} className={cancelBtnCls} data-testid="paie-portal-ts-save">
              {busy === "save" ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Save className="w-4 h-4 inline mr-1" />} Enregistrer
            </button>
            <button type="button" onClick={submit} disabled={!!busy} className={saveBtnCls} style={{ backgroundColor: primaryColor }} data-testid="paie-portal-ts-submit">
              {busy === "submit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {run.client_submitted_at ? "Soumettre de nouveau" : "Soumettre à mon gestionnaire"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
